import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const LiveGamesPreview = () => {
  const [liveStats, setLiveStats] = useState({});

  const games = [
    {
      id: 1,
      name: "Teen Patti Gold",
      image: "https://images.pexels.com/photos/1871508/pexels-photo-1871508.jpeg",
      players: 2847,
      minBet: 10,
      maxBet: 50000,
      category: "Card Games",
      isLive: true,
      popularity: "hot"
    },
    {
      id: 2,
      name: "Andar Bahar Royal",
      image: "https://images.pexels.com/photos/1871508/pexels-photo-1871508.jpeg",
      players: 1923,
      minBet: 5,
      maxBet: 25000,
      category: "Traditional",
      isLive: true,
      popularity: "trending"
    },
    {
      id: 3,
      name: "European Roulette",
      image: "https://images.pexels.com/photos/1871508/pexels-photo-1871508.jpeg",
      players: 1456,
      minBet: 20,
      maxBet: 100000,
      category: "Casino",
      isLive: true,
      popularity: "new"
    },
    {
      id: 4,
      name: "Dragon Tiger",
      image: "https://images.pexels.com/photos/1871508/pexels-photo-1871508.jpeg",
      players: 987,
      minBet: 15,
      maxBet: 30000,
      category: "Quick Games",
      isLive: true,
      popularity: "hot"
    },
    {
      id: 5,
      name: "Blackjack Premium",
      image: "https://images.pexels.com/photos/1871508/pexels-photo-1871508.jpeg",
      players: 756,
      minBet: 25,
      maxBet: 75000,
      category: "Card Games",
      isLive: true,
      popularity: "classic"
    },
    {
      id: 6,
      name: "Baccarat VIP",
      image: "https://images.pexels.com/photos/1871508/pexels-photo-1871508.jpeg",
      players: 543,
      minBet: 100,
      maxBet: 200000,
      category: "VIP Games",
      isLive: true,
      popularity: "premium"
    }
  ];

  useEffect(() => {
    // Simulate live player count updates
    const interval = setInterval(() => {
      setLiveStats(prev => {
        const newStats = { ...prev };
        games?.forEach(game => {
          const variance = Math.floor(Math.random() * 20) - 10;
          newStats[game.id] = Math.max(100, (newStats?.[game?.id] || game?.players) + variance);
        });
        return newStats;
      });
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const getPopularityBadge = (popularity) => {
    const badges = {
      hot: { icon: 'Flame', color: 'text-red-500', bg: 'bg-red-500/20', text: 'HOT' },
      trending: { icon: 'TrendingUp', color: 'text-green-500', bg: 'bg-green-500/20', text: 'TRENDING' },
      new: { icon: 'Sparkles', color: 'text-blue-500', bg: 'bg-blue-500/20', text: 'NEW' },
      premium: { icon: 'Crown', color: 'text-yellow-500', bg: 'bg-yellow-500/20', text: 'VIP' },
      classic: { icon: 'Star', color: 'text-purple-500', bg: 'bg-purple-500/20', text: 'CLASSIC' }
    };
    return badges?.[popularity] || badges?.classic;
  };

  const handleWhatsAppBooking = (gameName) => {
    const message = `Hello! I'm interested in playing ${gameName}. Can you help me get started with booking and provide more details?`;
    window.open(`https://wa.me/917704070169?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Icon name="Play" size={24} color="var(--color-primary)" />
            <span className="text-primary font-medium">LIVE GAMES</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-gray-900 mb-4">
            Join Live Gaming Action
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Experience real-time gaming with thousands of players. Premium games with instant WhatsApp support and guaranteed fair play.
          </p>
        </div>

        {/* Live Stats Bar */}
        <div className="bg-gradient-royal rounded-2xl p-6 mb-12 text-white">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="flex items-center justify-center space-x-2 mb-2">
                <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                <span className="text-sm opacity-90">LIVE NOW</span>
              </div>
              <div className="text-2xl font-bold">8,547</div>
              <div className="text-sm opacity-80">Active Players</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold">₹2.3Cr</div>
              <div className="text-sm opacity-80">Today's Winnings</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold">156</div>
              <div className="text-sm opacity-80">Winners This Hour</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold">4.9★</div>
              <div className="text-sm opacity-80">Player Rating</div>
            </div>
          </div>
        </div>

        {/* Games Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {games?.map((game) => {
            const badge = getPopularityBadge(game?.popularity);
            const currentPlayers = liveStats?.[game?.id] || game?.players;
            
            return (
              <div key={game?.id} className="bg-white rounded-2xl shadow-subtle hover:shadow-royal transition-royal overflow-hidden group">
                {/* Game Image */}
                <div className="relative h-48 overflow-hidden">
                  <Image
                    src={game?.image}
                    alt={game?.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  
                  {/* Live Indicator */}
                  <div className="absolute top-4 left-4 flex items-center space-x-2 bg-black/70 backdrop-blur-sm rounded-full px-3 py-1">
                    <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                    <span className="text-white text-xs font-medium">LIVE</span>
                  </div>

                  {/* Popularity Badge */}
                  <div className={`absolute top-4 right-4 ${badge?.bg} backdrop-blur-sm rounded-full px-3 py-1`}>
                    <div className="flex items-center space-x-1">
                      <Icon name={badge?.icon} size={12} color={badge?.color?.replace('text-', '')} />
                      <span className={`${badge?.color} text-xs font-bold`}>{badge?.text}</span>
                    </div>
                  </div>

                  {/* Play Overlay */}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center animate-pulse-golden">
                      <Icon name="Play" size={24} color="white" />
                    </div>
                  </div>
                </div>
                {/* Game Info */}
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-xl font-heading font-bold text-gray-900">{game?.name}</h3>
                    <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                      {game?.category}
                    </span>
                  </div>

                  {/* Player Count */}
                  <div className="flex items-center space-x-2 mb-4">
                    <Icon name="Users" size={16} color="var(--color-success)" />
                    <span className="text-success font-medium">{currentPlayers?.toLocaleString()} playing now</span>
                  </div>

                  {/* Betting Range */}
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <div className="text-sm text-gray-500">Min Bet</div>
                      <div className="font-bold text-gray-900">₹{game?.minBet}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-500">Max Bet</div>
                      <div className="font-bold text-gray-900">₹{game?.maxBet?.toLocaleString()}</div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-3">
                    <Button
                      variant="default"
                      fullWidth
                      onClick={() => handleWhatsAppBooking(game?.name)}
                      iconName="MessageCircle"
                      iconPosition="left"
                      className="bg-gradient-royal hover:shadow-glow"
                    >
                      Book via WhatsApp
                    </Button>
                    
                    <Link to="/games-showcase-gaming-palace" className="block">
                      <Button
                        variant="outline"
                        fullWidth
                        iconName="Info"
                        iconPosition="left"
                      >
                        View Details
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* View All Games CTA */}
        <div className="text-center mt-12">
          <Link to="/games-showcase-gaming-palace">
            <Button
              variant="outline"
              size="lg"
              iconName="ArrowRight"
              iconPosition="right"
              className="border-primary text-primary hover:bg-primary hover:text-white px-8 py-4"
            >
              Explore All 50+ Premium Games
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default LiveGamesPreview;